// JWT Token interfaces for type safety
import { jwtDecode } from "jwt-decode";

export interface JWTPayload {
  user_id: string | number;
  email?: string;
  role?: string;
  exp: number; // Expiration time (Unix timestamp)
  iat: number; // Issued at time (Unix timestamp)
  jti?: string; // JWT ID
  [key: string]: any; // Allow additional properties
}

export interface DecodedToken extends JWTPayload {
  user_id: string | number;
  exp: number;
  iat: number;
}

// JWT token utility functions with enhanced security
import Logger from "./logger";

export class TokenUtils {
  static decodeToken(token: string): DecodedToken | null {
    try {
      return jwtDecode<DecodedToken>(token);
    } catch (error) {
      Logger.error("Failed to decode JWT token:", error);
      return null;
    }
  }

  static isTokenExpired(token: string): boolean {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded) return true;

      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      Logger.error("Failed to check token expiry:", error);
      return true;
    }
  }

  static getTokenExpiryTime(token: string): Date | null {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded) return null;

      return new Date(decoded.exp * 1000);
    } catch (error) {
      Logger.error("Failed to get token expiry time:", error);
      return null;
    }
  }

  static getTimeUntilExpiry(token: string): number {
    try {
      const decoded = this.decodeToken(token);
      if (!decoded) return 0;

      const currentTime = Date.now() / 1000;
      const timeLeft = decoded.exp - currentTime;
      return Math.max(0, timeLeft * 1000); // Return milliseconds
    } catch (error) {
      Logger.error("Failed to calculate time until expiry:", error);
      return 0;
    }
  }
}
